import React, {useCallback} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {OnePaneLayoutFullWidthSection} from '@udemy/react-layout-components';
import {
    PersonalPlanBanner as AIEraCareerBannerComponent,
    PersonalPlanBannerProps,
    SubscriptionCTAClickEvent,
    SubscriptionOfferPresented,
    SubscriptionLearnMoreEvent,
} from '@udemy/subscription-browse';
import {
    AnnualSubscriptionPlanPricingOption,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    SubscriptionPlanContext,
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
    useConsumerSubscriptionValuePlan,
} from '@udemy/subscription-graphql';

type ValidPlan = (ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan) & {
    annualPlanOption: AnnualSubscriptionPlanPricingOption;
};

const hasValidPricing = (
    context: SubscriptionPlanContext<
        ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan
    > | null,
): context is ValidPlan => {
    return Boolean(
        context &&
            !context.isLoading &&
            'annualPlanOption' in context &&
            context.annualPlanOption?.monthlyPrice?.amount,
    );
};

const AIEraCareerBannerWrapper = ({children}: {children: React.ReactNode}) => (
    <OnePaneLayoutFullWidthSection>
        <OnePaneLayoutFullWidthSection.Content>{children}</OnePaneLayoutFullWidthSection.Content>
    </OnePaneLayoutFullWidthSection>
);

type AIEraCareerBannerFeatureProps = Omit<PersonalPlanBannerProps, 'ctaSlot'> & {
    subscriptionPlan: ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan;
    annualPlanOption: AnnualSubscriptionPlanPricingOption;
};

/**
 * AI Era Career Banner Implementation
 */
const AIEraCareerBannerFeature = ({
    subscriptionPlan,
    annualPlanOption,
    ...otherProps
}: AIEraCareerBannerFeatureProps) => {
    const subscriptionPrice = parseFloat(annualPlanOption.monthlyPrice.amount);

    const onCtaClick = useCallback(() => {
        Tracker.publishEvent(
            new SubscriptionCTAClickEvent(
                annualPlanOption.id,
                subscriptionPlan.productType,
                annualPlanOption.trial ? 'trial' : 'paid',
                'reimagine_career',
                null,
                undefined,
                [annualPlanOption.id],
            ),
        );
    }, [annualPlanOption, subscriptionPlan.productType]);

    const onLearnMoreClick = useCallback(() => {
        Tracker.publishEvent(
            new SubscriptionLearnMoreEvent(
                annualPlanOption.id,
                subscriptionPlan.productType,
                'reimagine_career',
                [annualPlanOption.id],
                null,
            ),
        );
    }, [annualPlanOption, subscriptionPlan.productType]);

    const trackImpression = () => {
        Tracker.publishEvent(
            new SubscriptionOfferPresented({
                subscriptionPlan,
                productType: SubscriptionPlanProductType.Consumersubscription,
                countryCode: annualPlanOption.monthlyPrice.currency ?? 'USD',
                uiRegion: 'reimagine_career',
            }),
        );
    };

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div>
                <AIEraCareerBannerComponent
                    subscriptionPrice={subscriptionPrice}
                    ctaUrl="/personal-plan/"
                    onCtaClick={onCtaClick}
                    onLearnMoreClick={onLearnMoreClick}
                    {...otherProps}
                />
            </div>
        </TrackImpression>
    );
};

/**
 * AIEraCareerBanner
 *
 * Displays a subscription banner.
 * Uses Value Plan if exists, else uses Personal Plan
 */
export const AIEraCareerBanner = () => {
    const {gettext} = useI18n();
    const valuePlanContext = useConsumerSubscriptionValuePlan();
    const personalPlanContext = useConsumerSubscriptionPlan();

    if (valuePlanContext?.isLoading || personalPlanContext?.isLoading) {
        return (
            <AIEraCareerBannerWrapper>
                <AIEraCareerBannerComponent isLoading={true} />
            </AIEraCareerBannerWrapper>
        );
    }

    const hasValuePlan = hasValidPricing(valuePlanContext);
    const plan = hasValuePlan ? valuePlanContext : personalPlanContext;

    if (!hasValidPricing(plan)) {
        return null;
    }

    const valuePlanProps = hasValuePlan
        ? {
              ctaUrl: '/pricing/',
              title: gettext('Reimagine your career for the AI era'),
              description: gettext(
                  'Future-proof your skills in the latest business and technology topics. Learn from real-world experts, right when you need it most.',
              ),
          }
        : {};

    return (
        <AIEraCareerBannerWrapper>
            <AIEraCareerBannerFeature
                subscriptionPlan={plan}
                annualPlanOption={plan.annualPlanOption}
                {...valuePlanProps}
            />
        </AIEraCareerBannerWrapper>
    );
};
